import { Link } from "@inertiajs/react";
import { Mail, Inbox } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * "Check your email" after asking for a sign-in or activation link. The
 * same page shows whether or not the address or code matched, so nobody
 * can use it to find out who has an account.
 */
export default function CheckEmailCard({ description, tips, retry, mailPreviewPath }) {
  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Mail className="h-6 w-6" />
        </div>
        <CardTitle as="h1">Check your email</CardTitle>
        <CardDescription className="leading-relaxed">{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {mailPreviewPath && <DevMailNotice path={mailPreviewPath} />}
        <div className="rounded-lg border border-border bg-background p-4 text-sm text-muted-foreground">
          <p className="mb-2 font-medium text-foreground">Didn't get it?</p>
          <ul className="list-disc space-y-1 pl-4">
            {tips.map((tip) => <li key={tip}>{tip}</li>)}
          </ul>
        </div>
        <div className="text-center">
          <Button variant="ghost" asChild>
            <Link href={retry.href}>{retry.label}</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Development only: the server sends a preview path so the loop can be
// tested without a real inbox.
function DevMailNotice({ path }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border-2 border-dashed border-warning/40 bg-warning/10 p-4 text-sm">
      <Inbox className="mt-0.5 h-5 w-5 shrink-0 text-warning-foreground" />
      <div className="flex-1">
        <p className="mb-1 font-semibold text-foreground">Dev mode</p>
        <p className="mb-3 text-muted-foreground">Emails land in letter_opener instead of a real inbox.</p>
        <Button size="sm" variant="outline" asChild>
          <a href={path} target="_blank" rel="noreferrer">Open letter_opener</a>
        </Button>
      </div>
    </div>
  );
}
